import { Box, Grid, Pagination, Typography } from "@mui/material";
import { Container, Tooltip } from "@/components/atoms";
import { FilterPanel, JobCard } from "@/components/organisms";
import { LoadingState, ErrorState, NoMatchState } from "@/components/views";
import { useSearchPageLogic } from "./SearchPage.logic";
import { useUserContext } from "@/providers/contexts";
import { JobPost } from "@/types/jobPost";

export const SearchPage = () => {
  const {
    resetAndHandlePageChange,
    jobPosts,
    totalPages,
    currentPage,
    handlePageChange,
    noMatchingResults,
    getNoResultsMessage,
    loading,
    error,
    onSortByDate,
    sortAscending,
  } = useSearchPageLogic();

  const { isAuthenticated, disabledBasicContent } = useUserContext();

  const disablePagination = !isAuthenticated || disabledBasicContent;

  if (error) return <ErrorState errors={error} />;
  if (loading) return <LoadingState />;

  return (
    <Container
      componentName="search"
      sx={{
        flexDirection: "column",
        justifyContent: "flex-start",
      }}
    >
      {
        <>
          <Box
            justifyContent="center"
            display="flex"
            sx={{ margin: 2 }}
            data-testid="search-page-title"
          >
            <Typography variant="title">Search for a job post</Typography>
          </Box>

          <Grid container spacing={4} data-testid="search-page-container">
            <Grid item xs={12} data-testid="filter-panel-grid">
              <FilterPanel
                resetFilters={resetAndHandlePageChange}
                page="search"
                onSortByDate={onSortByDate}
                sortAscending={sortAscending}
              />
            </Grid>

            <Grid item xs={12} data-testid="job-post-grid">
              {noMatchingResults && !loading && getNoResultsMessage() ? (
                <NoMatchState
                  message={getNoResultsMessage()}
                  onReset={resetAndHandlePageChange}
                />
              ) : (
                <>
                  <Grid container spacing={3} data-testid="job-cards-container">
                    {jobPosts?.map((jobPost: JobPost) => {
                      return (
                        <Grid item xs={12} key={jobPost.id}>
                          <JobCard jobPost={jobPost} />
                        </Grid>
                      );
                    })}
                  </Grid>
                  {disablePagination ? (
                    <Tooltip
                      componentName="search-page-pagination"
                      tooltipMessage={
                        "Upgrade your account to access this feature"
                      }
                      readMoreLink="/memberships"
                    >
                      <Box
                        data-testid="pagination-box"
                        sx={{
                          my: 4,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <Pagination
                          count={totalPages}
                          page={currentPage}
                          onChange={handlePageChange}
                          color="primary"
                          sx={{
                            opacity: disablePagination ? 0.4 : 1,
                            pointerEvents: disablePagination ? "none" : "auto",
                          }}
                        />
                      </Box>
                    </Tooltip>
                  ) : (
                    <Box
                      data-testid="pagination-box"
                      sx={{
                        my: 4,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                      />
                    </Box>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </>
      }
    </Container>
  );
};
